import {
  FeedCompositionRow,
  TenantConnectionCard,
  TenantConnectionsConfig,
} from "./portfolioConnections.types";

/**
 * Date the Clario ELF_Publisher hive snapshot was published to
 * gs://omniscience-vivo-data/clario/hive/ELF_Publisher/v1.
 */
const CLARIO_HIVE_SNAPSHOT_DATE = "2026-07-19T00:00:00Z";

const CLARIO_VENDOR = "Clario";

/** The two Clario demo trials, keyed exactly as in the URL / IAM trial ids. */
const UPB = "UPB-CP-03";
const CYTB = "CYTB323K12201";

/**
 * One card per raw feed in the Clario ELF_Publisher hive
 * (source systems: EXPERT cardiac/respiratory core lab, SW/SpiroSphere
 * eCOA, BioPACS imaging). Derived from the lake inventory in
 * CLARIO_COMPOSITION below.
 *
 * `description`/`facts` are transcribed from the Clario raw-data inventory
 * (clario__OmniSci_Raw_Data_Inventory.xlsx) and surface as a header tooltip.
 * Subject counts are the sponsor-reported figures from that inventory.
 *
 * `studies` mirrors each card's Scope/Studies fact and drives the trial-level
 * Data → Configuration screen (see getTenantTrialConnectionsConfig).
 */
const CLARIO_CONNECTIONS: TenantConnectionCard[] = [
  {
    datasource_type: "eCOA Questionnaires (SpiroSphere ODM)",
    studies: [UPB, CYTB],
    company_source: CLARIO_VENDOR,
    raw_data_access_type: "Cloud Storage",
    raw_data_cadence: "One-Time",
    date_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    description:
      "Electronic clinical outcome assessment (eCOA) form data — patient diaries and questionnaires captured on the SpiroSphere device, plus data-clarification requests and queries.",
    facts: [
      { label: "Format", value: "CDISC ODM XML v1.3.2 (+ Clario ERT ext.)" },
      { label: "Enriches", value: "Study · Site · Subject · Form · Query/DCR" },
      {
        label: "Scope",
        value: "UPB-CP-03 (317 subj) · CYTB323K12201 (97 subj)",
      },
      { label: "Loaded to", value: "Vivo Core DB" },
    ],
  },
  {
    datasource_type: "Spirometry & DLCO Results (EXPERT)",
    studies: [UPB, CYTB],
    company_source: CLARIO_VENDOR,
    raw_data_access_type: "Cloud Storage",
    raw_data_cadence: "One-Time",
    date_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    description:
      "Central-lab pulmonary function results from EXPERT — forced spirometry (FEV1, FVC, PEF…) and, for CYTB323K12201, DLCO diffusing capacity. Covers protocol, site/device, patient/device, transmittal and query data.",
    facts: [
      {
        label: "Format",
        value: "EXPERT JSON (measurement, overread, DCR/query)",
      },
      {
        label: "Studies",
        value: "800895 UPB-CP-03 (194) · 800958 CYTB323K12201 (597)",
      },
      { label: "Loaded to", value: "Vivo Core DB" },
    ],
  },
  {
    datasource_type: "ECG Results (EXPERT)",
    studies: [CYTB],
    company_source: CLARIO_VENDOR,
    raw_data_access_type: "Cloud Storage",
    raw_data_cadence: "One-Time",
    date_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    description:
      "12-lead ECG measurements, overread interpretations and DCRs/queries from EXPERT.",
    facts: [
      {
        label: "Format",
        value: "EXPERT JSON (measurement, overread, DCR/query)",
      },
      { label: "Study", value: "800978 · CYTB323K12201 (167 subj)" },
      { label: "Loaded to", value: "Vivo Core DB" },
    ],
  },
  {
    datasource_type: "Spirometry Flow-Volume Curves (EXPERT)",
    studies: [UPB, CYTB],
    company_source: CLARIO_VENDOR,
    raw_data_access_type: "Cloud Storage",
    raw_data_cadence: "One-Time",
    date_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    description:
      "Raw forced-spirometry flow-volume tracings — time, volume and flow samples delivered as three columns per effort.",
    facts: [
      { label: "Format", value: "CSV (base64 int32 @ 250 Hz)" },
      { label: "Studies", value: "800895 UPB-CP-03 · 800958 CYTB323K12201" },
      { label: "Loaded to", value: "Vivo Timeseries DB" },
    ],
  },
  {
    datasource_type: "ECG Waveforms (EXPERT AMPS)",
    studies: [CYTB],
    company_source: CLARIO_VENDOR,
    raw_data_access_type: "Cloud Storage",
    raw_data_cadence: "One-Time",
    date_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    description:
      "Source ECG waveform tracings in Clario's binary AMPS format, parsed to per-lead samples.",
    facts: [
      { label: "Format", value: "AMPS (binary)" },
      { label: "Study", value: "800978 · CYTB323K12201 (167 subj)" },
      { label: "Loaded to", value: "Vivo Timeseries DB" },
    ],
  },
  {
    datasource_type: "Logistics & Provisioning (Oracle EBS)",
    studies: [UPB, CYTB],
    company_source: CLARIO_VENDOR,
    raw_data_access_type: "Cloud Storage",
    raw_data_cadence: "One-Time",
    date_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    description:
      "Device shipment and equipment-inventory logistics from Oracle EBS (E-Business Suite) — provisioning of spirometers, ECG and imaging devices to sites and subjects.",
    facts: [
      { label: "Format", value: "CSV" },
      { label: "Scope", value: "Both protocols" },
      { label: "Loaded to", value: "Vivo Core DB" },
    ],
  },
  {
    datasource_type: "eCOA Device Analytics (SpiroSphere DD)",
    studies: [UPB, CYTB],
    company_source: CLARIO_VENDOR,
    raw_data_access_type: "Cloud Storage",
    raw_data_cadence: "One-Time",
    date_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    description:
      "Derived eCOA analytics — device compliance, enrollment and user-audit reporting from SpiroSphere (the 'DD' derived-data feed).",
    facts: [
      { label: "Format", value: "CSV (derived data)" },
      { label: "Scope", value: "UPB-CP-03 (317) · CYTB323K12201 (97)" },
      { label: "Loaded to", value: "Vivo Core DB" },
    ],
  },
  {
    datasource_type: "Study Design & Dictionaries (Apollo / StudyDesigner)",
    studies: [UPB, CYTB],
    company_source: CLARIO_VENDOR,
    raw_data_access_type: "Cloud Storage",
    raw_data_cadence: "One-Time",
    date_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    description:
      "Study configuration and design — visit/form mappings and reporting dictionaries (phases, enrollment groups) from Apollo, reportable form/item naming from the eCOA Study Designer, and CDISC ODM study-design metadata.",
    facts: [
      {
        label: "Format",
        value: "CSV (Apollo) · JSON (StudyDesigner) · ODM XML",
      },
      { label: "Scope", value: "Both protocols" },
      { label: "Loaded to", value: "Vivo Core DB" },
    ],
  },
  {
    datasource_type: "Imaging Operations (BioPACS)",
    studies: [UPB],
    company_source: CLARIO_VENDOR,
    raw_data_access_type: "Cloud Storage",
    raw_data_cadence: "One-Time",
    date_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    description:
      "BioPACS imaging-operations data — image collection, submissions, milestones/TAT and queries from the project-schema audit tables.",
    facts: [
      { label: "Format", value: "parquet (project schema)" },
      {
        label: "Scope",
        value: "UPB-CP-03 only · project prj_20004247 (274 subj)",
      },
      { label: "Loaded to", value: "Vivo Core DB" },
    ],
  },
  {
    datasource_type: "Imaging Reads & Adjudication (BioPACS)",
    studies: [UPB],
    company_source: CLARIO_VENDOR,
    raw_data_access_type: "Cloud Storage",
    raw_data_cadence: "One-Time",
    date_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: CLARIO_HIVE_SNAPSHOT_DATE,
    description:
      "BioPACS independent-read and adjudication data — reader overread measurements and interpretations, allocations, e-signatures and read-time logs from the read-schema audit tables.",
    facts: [
      { label: "Format", value: "parquet (read schema)" },
      { label: "Scope", value: "UPB-CP-03 · nasalpolyp + lmk_ct reads" },
      { label: "Loaded to", value: "Vivo Core DB" },
    ],
  },
];

/**
 * Feed inventory of gs://omniscience-vivo-data/clario/hive/ELF_Publisher/v1,
 * measured from a full object listing on 2026-07-19 (228,140 objects,
 * 3.4 GB). BioPACS rows are rolled up per schema_type; all other rows are
 * exact source/study/FileType hive partitions.
 *
 * "Loaded To" names the destination store the feed's data was loaded into:
 * "Vivo Core DB" (the relational core) or "Vivo Timeseries DB" (waveform /
 * flow-volume signal samples). Every raw file is also archived in Cloud
 * Storage. As of the 2026-07-19 load every feed has a store home (reader
 * adjudication and study-design metadata, previously unmodeled, now land in
 * the core DB).
 *
 * `format`/`description` (shown in the Feed-cell tooltip) are transcribed from
 * the Clario raw-data inventory (clario__OmniSci_Raw_Data_Inventory.xlsx).
 * Study attribution follows that inventory: EXPERT study 800978 (ECG) and the
 * BioPACS project/read schemas both belong to a single protocol
 * (CYTB323K12201 and UPB-CP-03 respectively), not to both — corrected here.
 */
const CLARIO_COMPOSITION: FeedCompositionRow[] = [
  {
    key: "BioPACS/project",
    sourceSystem: "BioPACS",
    study: "UPB-CP-03",
    feed: "project schema (64 audit tables)",
    files: 1372,
    bytes: 81091425,
    destination: "Vivo Core DB",
    format: "parquet",
    description:
      "Image-collection & project audit tables — submissions, milestones, turnaround (TAT) and operations, per the BioPACS project schema (project prj_20004247, nasal-polyp CT + landmark CT).",
  },
  {
    key: "BioPACS/read",
    sourceSystem: "BioPACS",
    study: "UPB-CP-03",
    feed: "read schema (32 audit tables)",
    files: 11380,
    bytes: 66756776,
    destination: "Vivo Core DB",
    format: "parquet",
    description:
      "Imaging overread & adjudication — reader measurements and interpretations, allocations, e-signatures and read-time logs (reads rd_20004247_nasalpolyp and rd_20004247_lmk_ct).",
  },
  {
    key: "source=EXPERT/environment=PROD/study=800958/FileType=ClinicalData",
    sourceSystem: "EXPERT",
    study: "CYTB323K12201 (800958)",
    feed: "ClinicalData",
    files: 13259,
    bytes: 138775490,
    destination: "Vivo Core DB",
    format: "JSON",
    description:
      "EXPERT clinical data — forced spirometry & DLCO results, plus protocol, site/device, patient/device, transmittal and query data (597 subjects).",
  },
  {
    key: "source=EXPERT/environment=PROD/study=800958/FileType=CurveData",
    sourceSystem: "EXPERT",
    study: "CYTB323K12201 (800958)",
    feed: "CurveData",
    files: 1,
    bytes: 208838684,
    destination: "Vivo Timeseries DB",
    format: "CSV",
    description:
      "Forced-spirometry flow-volume curves — time, volume and flow (three columns per effort).",
  },
  {
    key: "source=EXPERT/environment=PROD/study=800958/FileType=MetaData",
    sourceSystem: "EXPERT",
    study: "CYTB323K12201 (800958)",
    feed: "MetaData",
    files: 22,
    bytes: 3997846,
    destination: "Vivo Core DB",
    format: "XML",
    description: "EXPERT Protocol Designer study-design metadata.",
  },
  {
    key: "source=EXPERT/environment=PROD/study=800895/FileType=ClinicalData",
    sourceSystem: "EXPERT",
    study: "UPB-CP-03 (800895)",
    feed: "ClinicalData",
    files: 12665,
    bytes: 122155934,
    destination: "Vivo Core DB",
    format: "JSON",
    description:
      "EXPERT clinical data — forced spirometry results, plus protocol, site/device, patient/device, transmittal and query data (194 subjects).",
  },
  {
    key: "source=EXPERT/environment=PROD/study=800895/FileType=CurveData",
    sourceSystem: "EXPERT",
    study: "UPB-CP-03 (800895)",
    feed: "CurveData",
    files: 1,
    bytes: 127395789,
    destination: "Vivo Timeseries DB",
    format: "CSV",
    description:
      "Forced-spirometry flow-volume curves — time, volume and flow (three columns per effort).",
  },
  {
    key: "source=EXPERT/environment=PROD/study=800895/FileType=EBS",
    sourceSystem: "EXPERT",
    study: "UPB-CP-03 (800895)",
    feed: "EBS",
    files: 1,
    bytes: 428,
    destination: "Vivo Core DB",
    format: "CSV",
    description: "Oracle EBS logistics — device shipments and inventory.",
  },
  {
    key: "source=EXPERT/environment=PROD/study=800895/FileType=MetaData",
    sourceSystem: "EXPERT",
    study: "UPB-CP-03 (800895)",
    feed: "MetaData",
    files: 10,
    bytes: 1043432,
    destination: "Vivo Core DB",
    format: "XML",
    description: "EXPERT Protocol Designer study-design metadata.",
  },
  {
    key: "source=EXPERT/environment=PROD/study=800978/FileType=ClinicalData",
    sourceSystem: "EXPERT",
    study: "CYTB323K12201 (800978)",
    feed: "ClinicalData",
    files: 6408,
    bytes: 10943667,
    destination: "Vivo Core DB",
    format: "JSON",
    description:
      "EXPERT clinical data — 12-lead ECG measurements, overread interpretations and DCRs/queries (167 subjects).",
  },
  {
    key: "source=EXPERT/environment=PROD/study=800978/FileType=EBS",
    sourceSystem: "EXPERT",
    study: "CYTB323K12201 (800978)",
    feed: "EBS",
    files: 1607,
    bytes: 1410177067,
    destination: "Vivo Core DB",
    format: "CSV",
    description:
      "Oracle EBS logistics — cardiac device shipments and inventory.",
  },
  {
    key: "source=EXPERT/environment=PROD/study=800978/FileType=MetaData",
    sourceSystem: "EXPERT",
    study: "CYTB323K12201 (800978)",
    feed: "MetaData",
    files: 22,
    bytes: 1034326,
    destination: "Vivo Core DB",
    format: "XML",
    description: "EXPERT Protocol Designer study-design metadata (cardiac).",
  },
  {
    key: "source=EXPERT/environment=PROD/study=800978/FileType=amps",
    sourceSystem: "EXPERT",
    study: "CYTB323K12201 (800978)",
    feed: "amps",
    files: 358,
    bytes: 22365079,
    destination: "Vivo Timeseries DB",
    format: "AMPS (binary)",
    description: "Source ECG waveform tracings in Clario's binary AMPS format.",
  },
  {
    key: "source=SW/environment=PROD/study=CYTB323K12201/FileType=Apollo",
    sourceSystem: "SW",
    study: "CYTB323K12201",
    feed: "Apollo",
    files: 7,
    bytes: 31451,
    destination: "Vivo Core DB",
    format: "CSV",
    description:
      "Study configuration & design — visit/form mappings and reporting dictionaries (phases, enrollment groups).",
  },
  {
    key: "source=SW/environment=PROD/study=CYTB323K12201/FileType=DD",
    sourceSystem: "SW",
    study: "CYTB323K12201",
    feed: "DD",
    files: 189,
    bytes: 5272442,
    destination: "Vivo Core DB",
    format: "CSV",
    description:
      "eCOA analytics — compliance, enrollment and user-audit derived data.",
  },
  {
    key: "source=SW/environment=PROD/study=CYTB323K12201/FileType=MetaData",
    sourceSystem: "SW",
    study: "CYTB323K12201",
    feed: "MetaData",
    files: 4,
    bytes: 1741757,
    destination: "Vivo Core DB",
    format: "XML",
    description: "CDISC ODM v1.3.2 study-design metadata.",
  },
  {
    key: "source=SW/environment=PROD/study=CYTB323K12201/FileType=ODM",
    sourceSystem: "SW",
    study: "CYTB323K12201",
    feed: "ODM",
    files: 8514,
    bytes: 45429786,
    destination: "Vivo Core DB",
    format: "XML",
    description:
      "eCOA form data + DCR/queries — CDISC ODM v1.3.2 with Clario ERT extensions (site and query/DCR enrichment).",
  },
  {
    key: "source=SW/environment=PROD/study=CYTB323K12201/FileType=OracleEBS",
    sourceSystem: "SW",
    study: "CYTB323K12201",
    feed: "OracleEBS",
    files: 27,
    bytes: 470548,
    destination: "Vivo Core DB",
    format: "CSV",
    description: "Oracle EBS logistics — device shipments and inventory.",
  },
  {
    key: "source=SW/environment=PROD/study=CYTB323K12201/FileType=StudyDesigner",
    sourceSystem: "SW",
    study: "CYTB323K12201",
    feed: "StudyDesigner",
    files: 9,
    bytes: 34776147,
    destination: "Vivo Core DB",
    format: "JSON",
    description:
      "Clario eCOA Study Designer — reportable form and item naming.",
  },
  {
    key: "source=SW/environment=PROD/study=UPB-CP-03/FileType=Apollo",
    sourceSystem: "SW",
    study: "UPB-CP-03",
    feed: "Apollo",
    files: 7,
    bytes: 1782,
    destination: "Vivo Core DB",
    format: "CSV",
    description:
      "Study configuration & design — visit/form mappings and reporting dictionaries (phases, enrollment groups).",
  },
  {
    key: "source=SW/environment=PROD/study=UPB-CP-03/FileType=DD",
    sourceSystem: "SW",
    study: "UPB-CP-03",
    feed: "DD",
    files: 129,
    bytes: 73803006,
    destination: "Vivo Core DB",
    format: "CSV",
    description:
      "eCOA analytics — compliance, enrollment and user-audit derived data.",
  },
  {
    key: "source=SW/environment=PROD/study=UPB-CP-03/FileType=MetaData",
    sourceSystem: "SW",
    study: "UPB-CP-03",
    feed: "MetaData",
    files: 3,
    bytes: 1123655,
    destination: "Vivo Core DB",
    format: "XML",
    description: "CDISC ODM v1.3.2 study-design metadata.",
  },
  {
    key: "source=SW/environment=PROD/study=UPB-CP-03/FileType=ODM",
    sourceSystem: "SW",
    study: "UPB-CP-03",
    feed: "ODM",
    files: 172140,
    bytes: 1048966968,
    destination: "Vivo Core DB",
    format: "XML",
    description:
      "eCOA form data + DCR/queries — CDISC ODM v1.3.2 with Clario ERT extensions (study, site, subject, form and query/DCR enrichment).",
  },
  {
    key: "source=SW/environment=PROD/study=UPB-CP-03/FileType=OracleEBS",
    sourceSystem: "SW",
    study: "UPB-CP-03",
    feed: "OracleEBS",
    files: 2,
    bytes: 142,
    destination: "Vivo Core DB",
    format: "CSV",
    description: "Oracle EBS logistics — device shipments and inventory.",
  },
  {
    key: "source=SW/environment=PROD/study=UPB-CP-03/FileType=StudyDesigner",
    sourceSystem: "SW",
    study: "UPB-CP-03",
    feed: "StudyDesigner",
    files: 3,
    bytes: 23036,
    destination: "Vivo Core DB",
    format: "JSON",
    description:
      "Clario eCOA Study Designer — reportable form and item naming.",
  },
];

/**
 * Fallback set for tenants without a hand-built config. Every entry is a
 * source external to the trials — site/investigator registries, commercial
 * trial intelligence, a site survey — so the whole set is portfolio-scoped.
 */
const DEFAULT_CONNECTIONS: TenantConnectionCard[] = [
  {
    external: true,
    datasource_type: "Site & Investigator Data (AODM)",
    company_source: "AODM",
    raw_data_access_type: "Cloud Storage",
    raw_data_cadence: "Ad Hoc",
    date_uploaded: "2026-07-01T00:00:00Z",
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: "2026-07-01T00:00:00Z",
  },
  {
    external: true,
    datasource_type: "Data Quality System (DQS)",
    company_source: "IQVIA",
    raw_data_access_type: "Cloud Storage",
    raw_data_cadence: "Ad Hoc",
    date_uploaded: "2026-06-29T00:00:00Z",
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: "2026-06-29T00:00:00Z",
  },
  {
    external: true,
    datasource_type: "Investigator Intelligence (Citeline)",
    company_source: "Citeline",
    raw_data_access_type: "Cloud Storage",
    raw_data_cadence: "Ad Hoc",
    date_uploaded: "2026-06-29T00:00:00Z",
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: "2026-06-29T00:00:00Z",
  },
  {
    external: true,
    datasource_type: "Organization Intelligence (Citeline)",
    company_source: "Citeline",
    raw_data_access_type: "Cloud Storage",
    raw_data_cadence: "Ad Hoc",
    date_uploaded: "2026-06-29T00:00:00Z",
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: "2026-06-29T00:00:00Z",
  },
  {
    external: true,
    datasource_type: "Trial Intelligence (Citeline)",
    company_source: "Citeline",
    raw_data_access_type: "Cloud Storage",
    raw_data_cadence: "Ad Hoc",
    date_uploaded: "2026-06-29T00:00:00Z",
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: "2026-06-29T00:00:00Z",
  },
  {
    external: true,
    datasource_type: "Trial-Investigator Links (Citeline)",
    company_source: "Citeline",
    raw_data_access_type: "Cloud Storage",
    raw_data_cadence: "Ad Hoc",
    date_uploaded: "2026-06-29T00:00:00Z",
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: "2026-06-29T00:00:00Z",
  },
  {
    external: true,
    datasource_type: "Site Profile Survey (Alchemer)",
    company_source: "Alchemer",
    raw_data_access_type: "Cloud Storage",
    raw_data_cadence: "Ad Hoc",
    date_uploaded: null,
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: null,
  },
];

/**
 * Date the Florence eBinders vendor demo bundle was generated
 * (florence_ebinders_demo_20260717/README.md).
 */
const FLORENCE_BUNDLE_DATE = "2026-07-17T22:04:37Z";

const FLORENCE_VENDOR = "Florence";

/**
 * One card per logical feed in the Florence eBinders vendor demo bundle
 * (12 synthetic ISF workspaces derived from a UAT source team; identifiers
 * and PII remapped, FK-validated). Counts are parsed record counts from the
 * bundle's validation_report.json.
 */
const FLORENCE_CONNECTIONS: TenantConnectionCard[] = [
  {
    datasource_type: "Study & Site Registry (eBinders ISF)",
    company_source: FLORENCE_VENDOR,
    raw_data_access_type: "File Export",
    raw_data_cadence: "One-Time",
    date_uploaded: FLORENCE_BUNDLE_DATE,
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: FLORENCE_BUNDLE_DATE,
    description:
      "Study profiles (protocol, NCT/EudraCT/REC identifiers, phase, status, CRO) and site records (PI, address) from each eBinders workspace.",
    facts: [
      { label: "Format", value: "CSV (Fivetran relational export)" },
      { label: "Scope", value: "28 studies · 28 sites across 12 teams" },
      { label: "Loaded to", value: "Vivo Core DB" },
    ],
  },
  {
    datasource_type: "Document Inventory (eBinders ISF)",
    company_source: FLORENCE_VENDOR,
    raw_data_access_type: "File Export",
    raw_data_cadence: "One-Time",
    date_uploaded: FLORENCE_BUNDLE_DATE,
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: FLORENCE_BUNDLE_DATE,
    description:
      "Binder/folder hierarchy and document metadata with versions — expected-document placeholders vs collected content, due/expiration dates, form status. File bodies remain in Florence S3 (KMS pointers only).",
    facts: [
      { label: "Format", value: "CSV (metadata; no binaries)" },
      {
        label: "Scope",
        value: "225 binders · 24,665 folders · 10,735 doc versions",
      },
      { label: "Loaded to", value: "Vivo Core DB" },
    ],
  },
  {
    datasource_type: "Signatures & Sign-off Workflows (eBinders)",
    company_source: FLORENCE_VENDOR,
    raw_data_access_type: "File Export",
    raw_data_cadence: "One-Time",
    date_uploaded: FLORENCE_BUNDLE_DATE,
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: FLORENCE_BUNDLE_DATE,
    description:
      "Signature requests (Review / Acknowledge / Approval routing) and completed signature events with signer identity, captured per document version.",
    facts: [
      { label: "Format", value: "CSV + embedded JSON events" },
      { label: "Scope", value: "280 requests · completed events per document" },
      { label: "Loaded to", value: "Vivo Core DB (completed signatures)" },
    ],
  },
  {
    datasource_type: "Monitor Review Module Status (eBinders MRM)",
    company_source: FLORENCE_VENDOR,
    raw_data_access_type: "File Export",
    raw_data_cadence: "One-Time",
    date_uploaded: FLORENCE_BUNDLE_DATE,
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: FLORENCE_BUNDLE_DATE,
    description:
      "Document-level monitor review statuses and reviewer/approver audit trail from the eBinders analytics mart (dbt: report_document_mrm_details).",
    facts: [
      { label: "Format", value: "CSV (dbt analytics mart)" },
      { label: "Scope", value: "660 document-version review records" },
      { label: "Loaded to", value: "Vivo Core DB" },
    ],
  },
  {
    datasource_type: "Workspaces, Roles & Labels (eBinders)",
    company_source: FLORENCE_VENDOR,
    raw_data_access_type: "File Export",
    raw_data_cadence: "One-Time",
    date_uploaded: FLORENCE_BUNDLE_DATE,
    data_lock_date: null,
    raw_data_fetch_failure_datetime: null,
    data_uploaded: FLORENCE_BUNDLE_DATE,
    description:
      "Team workspaces with sponsor linkage (Salesforce accounts), role definitions, user-role assignments, permission grants, label and document-category dictionaries.",
    facts: [
      { label: "Format", value: "CSV" },
      {
        label: "Scope",
        value: "12 teams · 48 roles · 612 permissions · 147 categories",
      },
      {
        label: "Loaded to",
        value: "Vivo Core DB (orgs + categories); RBAC not mapped",
      },
    ],
  },
];

/**
 * Table inventory of florence_ebinders_demo_20260717, measured from the
 * bundle on disk (parsed record counts from validation_report.json; byte
 * sizes from the CSV files). Bundle-level feeds are team-scoped, so rows
 * are not attributed to individual trial ids.
 */
const FLORENCE_BUNDLE = "florence_ebinders_demo_20260717";

const FLORENCE_COMPOSITION: FeedCompositionRow[] = [
  {
    key: `${FLORENCE_BUNDLE}/studies`,
    sourceSystem: "eBinders",
    study: FLORENCE_BUNDLE,
    feed: "studies",
    files: 28,
    bytes: 12593,
    destination: "Vivo Core DB",
    format: "CSV",
    description:
      "Study profiles per team: protocol/NCT/EudraCT/REC ids, phase, status, type, CRO, sponsor.",
  },
  {
    key: `${FLORENCE_BUNDLE}/sites`,
    sourceSystem: "eBinders",
    study: FLORENCE_BUNDLE,
    feed: "sites",
    files: 28,
    bytes: 10950,
    destination: "Vivo Core DB",
    format: "CSV",
    description:
      "Site records: PI, custom site id, sparse real-world addresses.",
  },
  {
    key: `${FLORENCE_BUNDLE}/sponsors_teams`,
    sourceSystem: "eBinders",
    study: FLORENCE_BUNDLE,
    feed: "sponsors + teams",
    files: 16,
    bytes: 28995,
    destination: "Vivo Core DB",
    format: "CSV",
    description: "Sponsor accounts (Salesforce-style ids) and team workspaces.",
  },
  {
    key: `${FLORENCE_BUNDLE}/binders`,
    sourceSystem: "eBinders",
    study: FLORENCE_BUNDLE,
    feed: "binders",
    files: 225,
    bytes: 101537,
    destination: "Vivo Core DB",
    format: "CSV",
    description: "Top-level document containers.",
  },
  {
    key: `${FLORENCE_BUNDLE}/folders`,
    sourceSystem: "eBinders",
    study: FLORENCE_BUNDLE,
    feed: "folders",
    files: 24665,
    bytes: 15572468,
    destination: "Vivo Core DB",
    format: "CSV",
    description: "Folder hierarchy (TMF reference-model paths).",
  },
  {
    key: `${FLORENCE_BUNDLE}/documents`,
    sourceSystem: "eBinders",
    study: FLORENCE_BUNDLE,
    feed: "documents",
    files: 10735,
    bytes: 13247596,
    destination: "Vivo Core DB",
    format: "CSV",
    description:
      "Document metadata incl. versions — 8,094 placeholders / 2,641 content; completed signature events loaded to Vivo Core DB.",
  },
  {
    key: `${FLORENCE_BUNDLE}/signature_requests`,
    sourceSystem: "eBinders",
    study: FLORENCE_BUNDLE,
    feed: "signature_requests",
    files: 280,
    bytes: 161257,
    destination: "Vivo Core DB",
    format: "CSV",
    description:
      "Signature workflow requests (reason, method, due date, routing).",
  },
  {
    key: `${FLORENCE_BUNDLE}/report_document_mrm_details`,
    sourceSystem: "eBinders",
    study: FLORENCE_BUNDLE,
    feed: "MRM mart",
    files: 660,
    bytes: 373714,
    destination: "Vivo Core DB",
    format: "CSV",
    description:
      "Monitor Review Module document statuses + audit trail (dbt mart).",
  },
  {
    key: `${FLORENCE_BUNDLE}/rbac`,
    sourceSystem: "eBinders",
    study: FLORENCE_BUNDLE,
    feed: "roles + user_roles + permissions",
    files: 944,
    bytes: 329862,
    destination: "Not mapped",
    format: "CSV",
    description:
      "Workspace RBAC: role definitions, user-role assignments, permission grants.",
  },
  {
    key: `${FLORENCE_BUNDLE}/labels_categories`,
    sourceSystem: "eBinders",
    study: FLORENCE_BUNDLE,
    feed: "labels + document_categories",
    files: 219,
    bytes: 94474,
    destination: "Vivo Core DB (categories)",
    format: "CSV",
    description:
      "Label definitions and per-team document-category dictionaries.",
  },
  {
    key: `${FLORENCE_BUNDLE}/study_entities`,
    sourceSystem: "eBinders",
    study: FLORENCE_BUNDLE,
    feed: "study_entities",
    files: 23,
    bytes: 7222,
    destination: "Vivo Core DB",
    format: "CSV",
    description: "Study ↔ binder linkage.",
  },
];

const TENANT_CONFIGS: Record<string, TenantConnectionsConfig> = {
  clario: {
    connections: CLARIO_CONNECTIONS,
    composition: CLARIO_COMPOSITION,
  },
  florence: {
    connections: FLORENCE_CONNECTIONS,
    composition: FLORENCE_COMPOSITION,
    portfolioListsAllFeeds: true,
  },
};

const DEFAULT_CONFIG: TenantConnectionsConfig = {
  connections: DEFAULT_CONNECTIONS,
};

/**
 * Obfuscated cloud hostnames don't carry the tenant name, so their first label
 * has to be translated back to the slug used by TENANT_CONFIGS.
 */
const TENANT_SLUG_ALIASES: Record<string, string> = {
  "demo-f6e": "florence",
  "demo-c4o": "clario",
  "demo-t6a": "takeda",
};

/**
 * Tenant slug from a hostname: the first dot-separated label
 * (clario.omniscivivo.bio and clario.localhost both → "clario"), after
 * translating the obfuscated cloud hostnames (demo-f6e.… → "florence").
 */
export function getTenantSlug(hostname: string): string {
  const label = hostname.split(".")[0].toLowerCase();
  return TENANT_SLUG_ALIASES[label] ?? label;
}

export function getTenantConnectionsConfig(
  hostname: string,
): TenantConnectionsConfig {
  return TENANT_CONFIGS[getTenantSlug(hostname)] ?? DEFAULT_CONFIG;
}

/**
 * Portfolio-scoped view of a tenant's static connections config: only the
 * sources external to the trials, unless the tenant sets
 * `portfolioListsAllFeeds` (florence, whose eBinders bundle spans every
 * trial). A tenant whose feeds all carry per-trial conduct data (clario's
 * vendor systems) has no external sources and gets an empty connections list —
 * those cards belong on each trial's Data → Configuration screen, which
 * {@link getTenantTrialConnectionsConfig} still supplies in full.
 *
 * The feed-composition inventory is left alone: it describes the raw lake
 * behind the tenant, not a per-trial connection.
 */
export function getTenantExternalConnectionsConfig(
  hostname: string,
): TenantConnectionsConfig {
  const config = getTenantConnectionsConfig(hostname);
  if (config.portfolioListsAllFeeds) return config;
  return {
    ...config,
    connections: config.connections.filter((card) => card.external),
  };
}

/**
 * Composition rows carry the trial id either bare ("UPB-CP-03") or with the
 * vendor study number appended ("CYTB323K12201 (800958)").
 */
function compositionRowCoversTrial(
  row: FeedCompositionRow,
  trialId: string,
): boolean {
  return row.study === trialId || row.study.startsWith(`${trialId} (`);
}

/**
 * Trial-scoped view of a tenant's static connections config: the portfolio
 * cards limited to feeds covering the trial, and the feed-composition rows
 * for that trial's studies. Returns null for tenants without a static config
 * — their trial screens are driven entirely by seeded data_connection rows.
 */
export function getTenantTrialConnectionsConfig(
  hostname: string,
  trialId: string,
): TenantConnectionsConfig | null {
  const config = TENANT_CONFIGS[getTenantSlug(hostname)];
  if (!config) return null;

  const connections = config.connections.filter(
    (card) => !card.studies || card.studies.includes(trialId),
  );
  const composition = config.composition?.filter((row) =>
    compositionRowCoversTrial(row, trialId),
  );

  return {
    connections,
    composition: composition?.length ? composition : undefined,
  };
}

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"];

export function formatBytes(bytes: number): string {
  if (bytes <= 0) return "0 B";
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1000 && unitIndex < BYTE_UNITS.length - 1) {
    value /= 1000;
    unitIndex += 1;
  }
  const digits = value >= 100 || unitIndex === 0 ? 0 : 1;
  return `${value.toFixed(digits)} ${BYTE_UNITS[unitIndex]}`;
}
